import { useState, useCallback, useEffect } from 'react';
import './AirportFeatures.css';
import { getAccessToken } from '../utils/auth';
import {
  getAirportFeatures,
  syncAirportFeaturesFromOsm,
  saveAirportFeature,
  deleteAirportFeature,
  detectAirportFeatureConflicts,
  resolveAirportFeatureConflict
} from '../utils/httpApi';
import { isApiSuccess, getFirstApiError } from '../utils/api';

// Feature type configuration
const FEATURE_TYPES = [
  { value: 'runway', label: 'Runway', color: '#e74c3c' },
  { value: 'taxiway', label: 'Taxiway', color: '#f39c12' },
  { value: 'apron', label: 'Apron', color: '#9b59b6' },
  { value: 'terminal', label: 'Terminal', color: '#3498db' },
  { value: 'parking_position', label: 'Stand/Gate', color: '#2ecc71' },
  { value: 'hangar', label: 'Hangar', color: '#1abc9c' },
  { value: 'tower', label: 'Tower', color: '#e67e22' },
  { value: 'navaid', label: 'Navaid', color: '#95a5a6' }
];

const SURFACE_TYPES = [
  'asphalt', 'concrete', 'grass', 'gravel', 'dirt', 'sand', 'unpaved', 'paved', 'unknown'
];

function AirportFeatures() {
  // State
  const [icaoCode, setIcaoCode] = useState('');
  const [searchedIcao, setSearchedIcao] = useState('');
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [featureTypeFilter, setFeatureTypeFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');

  // Edit state
  const [editingFeature, setEditingFeature] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Conflict state
  const [conflicts, setConflicts] = useState([]);
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [resolvingConflict, setResolvingConflict] = useState(null);

  // Clear messages after timeout
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Load features for airport
  const loadFeatures = useCallback(async (icao) => {
    if (!icao || icao.length < 3) {
      setError('Please enter a valid ICAO code (3-4 characters)');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = getAccessToken();
      const response = await getAirportFeatures(icao.toUpperCase(), token);

      if (isApiSuccess(response)) {
        // Flatten all feature types into a single array with type indicator
        const allFeatures = [];

        const addFeatures = (items, type) => {
          if (items && Array.isArray(items)) {
            items.forEach(f => allFeatures.push({ ...f, featureType: type }));
          }
        };

        // Use raw response data, not the transformed 'features'
        addFeatures(response.runways, 'runway');
        addFeatures(response.taxiways, 'taxiway');
        addFeatures(response.aprons, 'apron');
        addFeatures(response.terminals, 'terminal');
        addFeatures(response.parkingPositions, 'parking_position');
        addFeatures(response.hangars, 'hangar');
        addFeatures(response.navaids, 'navaid');
        if (response.tower) {
          allFeatures.push({ ...response.tower, featureType: 'tower' });
        }

        setFeatures(allFeatures);
        setSearchedIcao(icao.toUpperCase());
        setSuccessMessage(`Loaded ${allFeatures.length} features for ${icao.toUpperCase()}`);
      } else {
        setError(getFirstApiError(response) || 'Failed to load airport features');
        setFeatures([]);
      }
    } catch (err) {
      console.error('Error loading features:', err);
      setError('Failed to load airport features: ' + err.message);
      setFeatures([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Check for conflicts
  const checkConflicts = useCallback(async () => {
    if (!searchedIcao) return;

    try {
      const token = getAccessToken();
      const response = await detectAirportFeatureConflicts(searchedIcao, token);

      if (isApiSuccess(response) && response.conflicts && response.conflicts.length > 0) {
        setConflicts(response.conflicts);
        setShowConflictDialog(true);
      } else {
        setConflicts([]);
      }
    } catch (err) {
      console.error('Error checking conflicts:', err);
    }
  }, [searchedIcao]);

  // Sync features from OSM
  const handleSync = useCallback(async (forceFullSync = false) => {
    if (!searchedIcao) {
      setError('Please search for an airport first');
      return;
    }

    setSyncing(true);
    setError(null);

    try {
      const token = getAccessToken();
      const response = await syncAirportFeaturesFromOsm(searchedIcao, token, 5, forceFullSync);

      if (isApiSuccess(response)) {
        const { inserted, updated, deleted, conflicts: conflictCount } = response;
        setSuccessMessage(
          `Sync complete: ${inserted || 0} inserted, ${updated || 0} updated, ${deleted || 0} deleted` +
          (conflictCount > 0 ? `, ${conflictCount} conflicts detected` : '')
        );

        // Reload features after sync
        await loadFeatures(searchedIcao);

        // Check for conflicts
        if (conflictCount > 0) {
          await checkConflicts();
        }
      } else {
        setError(getFirstApiError(response) || 'Sync failed');
      }
    } catch (err) {
      console.error('Sync error:', err);
      setError('Sync failed: ' + err.message);
    } finally {
      setSyncing(false);
    }
  }, [searchedIcao, loadFeatures, checkConflicts]);

  // Resolve a conflict
  const handleResolveConflict = useCallback(async (featureId, resolution) => {
    setResolvingConflict(featureId);

    try {
      const token = getAccessToken();
      const response = await resolveAirportFeatureConflict(featureId, resolution, token);

      if (isApiSuccess(response)) {
        setConflicts(prev => prev.filter(c => c.localVersion?.id !== featureId));
        setSuccessMessage(`Conflict resolved: ${resolution === 'KEEP_LOCAL' ? 'Local version kept' : 'OSM version applied'}`);

        // Reload features
        await loadFeatures(searchedIcao);

        // Close dialog if no more conflicts
        if (conflicts.length <= 1) {
          setShowConflictDialog(false);
        }
      } else {
        setError(getFirstApiError(response) || 'Failed to resolve conflict');
      }
    } catch (err) {
      console.error('Error resolving conflict:', err);
      setError('Failed to resolve conflict: ' + err.message);
    } finally {
      setResolvingConflict(null);
    }
  }, [searchedIcao, conflicts.length, loadFeatures]);

  // Start editing a feature
  const startEdit = (feature) => {
    setEditingFeature(feature.id || feature.osmId);
    setEditForm({
      id: feature.id,
      airportIcao: searchedIcao,
      osmId: feature.osmId,
      osmType: feature.osmType,
      featureType: feature.featureType,
      name: feature.name || '',
      ref: feature.ref || '',
      surface: feature.surface || '',
      lengthMeters: feature.lengthMeters || feature.length || '',
      widthMeters: feature.widthMeters || feature.width || '',
      latitude: feature.latitude || '',
      longitude: feature.longitude || '',
      startLat: feature.startLat || '',
      startLon: feature.startLon || '',
      endLat: feature.endLat || '',
      endLon: feature.endLon || '',
      heading: feature.heading || '',
      lighted: feature.lighted || false,
      closed: feature.closed || false,
      source: feature.source || 'OSM'
    });
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingFeature(null);
    setEditForm({});
  };

  // Save feature
  const saveFeature = async () => {
    setSaving(true);
    setError(null);

    try {
      const token = getAccessToken();

      // Prepare feature object for API
      const featureToSave = {
        ...editForm,
        lengthMeters: editForm.lengthMeters ? parseFloat(editForm.lengthMeters) : null,
        widthMeters: editForm.widthMeters ? parseFloat(editForm.widthMeters) : null,
        latitude: editForm.latitude ? parseFloat(editForm.latitude) : null,
        longitude: editForm.longitude ? parseFloat(editForm.longitude) : null,
        startLat: editForm.startLat ? parseFloat(editForm.startLat) : null,
        startLon: editForm.startLon ? parseFloat(editForm.startLon) : null,
        endLat: editForm.endLat ? parseFloat(editForm.endLat) : null,
        endLon: editForm.endLon ? parseFloat(editForm.endLon) : null,
        heading: editForm.heading ? parseFloat(editForm.heading) : null
      };

      const response = await saveAirportFeature(featureToSave, token);

      if (isApiSuccess(response)) {
        setSuccessMessage(`Feature ${response.action === 'CREATED' ? 'created' : 'updated'} successfully`);
        cancelEdit();
        await loadFeatures(searchedIcao);
      } else {
        setError(getFirstApiError(response) || 'Failed to save feature');
      }
    } catch (err) {
      console.error('Error saving feature:', err);
      setError('Failed to save feature: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Delete feature
  const handleDelete = async (feature) => {
    if (!window.confirm(`Delete ${feature.featureType} "${feature.ref || feature.name || 'unnamed'}"?`)) {
      return;
    }

    try {
      const token = getAccessToken();
      const response = await deleteAirportFeature(feature.id, searchedIcao, token);

      if (isApiSuccess(response)) {
        setSuccessMessage('Feature deleted successfully');
        await loadFeatures(searchedIcao);
      } else {
        setError(getFirstApiError(response) || 'Failed to delete feature');
      }
    } catch (err) {
      console.error('Error deleting feature:', err);
      setError('Failed to delete feature: ' + err.message);
    }
  };

  // Filter features
  const filteredFeatures = features.filter(f => {
    if (featureTypeFilter !== 'all' && f.featureType !== featureTypeFilter) return false;
    if (sourceFilter !== 'all' && f.source !== sourceFilter) return false;
    return true;
  });

  // Get feature type config
  const getFeatureTypeConfig = (type) => {
    return FEATURE_TYPES.find(t => t.value === type) || { label: type, color: '#999' };
  };

  // Format coordinate
  const formatCoord = (val) => {
    return val != null ? val.toFixed(6) : '-';
  };

  // Handle search form submit
  const handleSearch = (e) => {
    e.preventDefault();
    loadFeatures(icaoCode);
  };

  return (
    <div className="airport-features">
      {/* Search Section */}
      <div className="af-search-section">
        <form onSubmit={handleSearch} className="af-search-form">
          <input
            type="text"
            value={icaoCode}
            onChange={(e) => setIcaoCode(e.target.value.toUpperCase())}
            placeholder="Enter ICAO code (e.g., EGLL)"
            maxLength={4}
            className="af-search-input"
          />
          <button type="submit" disabled={loading || icaoCode.length < 3} className="af-search-btn">
            {loading ? 'Loading...' : 'Load Features'}
          </button>
        </form>

        {searchedIcao && (
          <div className="af-actions">
            <button
              onClick={() => handleSync(false)}
              disabled={syncing}
              className="af-sync-btn"
            >
              {syncing ? 'Syncing...' : 'Sync from OSM'}
            </button>
            <button
              onClick={() => handleSync(true)}
              disabled={syncing}
              className="af-sync-btn af-sync-full"
            >
              Force Full Sync
            </button>
            <button
              onClick={checkConflicts}
              className="af-conflicts-btn"
            >
              Check Conflicts
            </button>
          </div>
        )}
      </div>

      {/* Messages */}
      {error && <div className="af-error">{error}</div>}
      {successMessage && <div className="af-success">{successMessage}</div>}

      {/* Filters */}
      {searchedIcao && (
        <div className="af-filters">
          <div className="af-filter">
            <label>Type:</label>
            <select value={featureTypeFilter} onChange={(e) => setFeatureTypeFilter(e.target.value)}>
              <option value="all">All Types</option>
              {FEATURE_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div className="af-filter">
            <label>Source:</label>
            <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
              <option value="all">All Sources</option>
              <option value="OSM">OSM</option>
              <option value="MANUAL">Manual</option>
            </select>
          </div>
          <div className="af-count">
            Showing {filteredFeatures.length} of {features.length} features
          </div>
        </div>
      )}

      {/* Features Table */}
      {searchedIcao && (
        <div className="af-table-container">
          <table className="af-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Ref/Name</th>
                <th>Surface</th>
                <th>Dimensions</th>
                <th>Center</th>
                <th>Source</th>
                <th>Modified</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredFeatures.length === 0 ? (
                <tr>
                  <td colSpan="8" className="af-empty">
                    {features.length === 0
                      ? 'No features found. Click "Sync from OSM" to import features.'
                      : 'No features match the current filters.'}
                  </td>
                </tr>
              ) : (
                filteredFeatures.map((feature, idx) => {
                  const typeConfig = getFeatureTypeConfig(feature.featureType);
                  const isEditing = editingFeature === (feature.id || feature.osmId);

                  if (isEditing) {
                    return (
                      <tr key={feature.id || feature.osmId || idx} className="af-edit-row">
                        <td colSpan="8">
                          <div className="af-edit-form">
                            <h4>Edit {typeConfig.label}: {feature.ref || feature.name || 'Unnamed'}</h4>
                            <div className="af-edit-grid">
                              <div className="af-edit-field">
                                <label>Type</label>
                                <select
                                  value={editForm.featureType}
                                  onChange={(e) => setEditForm({ ...editForm, featureType: e.target.value })}
                                >
                                  {FEATURE_TYPES.map(t => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="af-edit-field">
                                <label>Ref/Designation</label>
                                <input
                                  type="text"
                                  value={editForm.ref}
                                  onChange={(e) => setEditForm({ ...editForm, ref: e.target.value })}
                                  placeholder="e.g., 09L/27R"
                                />
                              </div>
                              <div className="af-edit-field">
                                <label>Name</label>
                                <input
                                  type="text"
                                  value={editForm.name}
                                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                  placeholder="Feature name"
                                />
                              </div>
                              <div className="af-edit-field">
                                <label>Surface</label>
                                <select
                                  value={editForm.surface}
                                  onChange={(e) => setEditForm({ ...editForm, surface: e.target.value })}
                                >
                                  <option value="">Select surface</option>
                                  {SURFACE_TYPES.map(s => (
                                    <option key={s} value={s}>{s}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="af-edit-field">
                                <label>Length (m)</label>
                                <input
                                  type="number"
                                  value={editForm.lengthMeters}
                                  onChange={(e) => setEditForm({ ...editForm, lengthMeters: e.target.value })}
                                  step="0.1"
                                />
                              </div>
                              <div className="af-edit-field">
                                <label>Width (m)</label>
                                <input
                                  type="number"
                                  value={editForm.widthMeters}
                                  onChange={(e) => setEditForm({ ...editForm, widthMeters: e.target.value })}
                                  step="0.1"
                                />
                              </div>
                              <div className="af-edit-field">
                                <label>Center Lat</label>
                                <input
                                  type="number"
                                  value={editForm.latitude}
                                  onChange={(e) => setEditForm({ ...editForm, latitude: e.target.value })}
                                  step="0.000001"
                                />
                              </div>
                              <div className="af-edit-field">
                                <label>Center Lon</label>
                                <input
                                  type="number"
                                  value={editForm.longitude}
                                  onChange={(e) => setEditForm({ ...editForm, longitude: e.target.value })}
                                  step="0.000001"
                                />
                              </div>
                              {(editForm.featureType === 'runway') && (
                                <>
                                  <div className="af-edit-field">
                                    <label>Start Lat</label>
                                    <input
                                      type="number"
                                      value={editForm.startLat}
                                      onChange={(e) => setEditForm({ ...editForm, startLat: e.target.value })}
                                      step="0.000001"
                                    />
                                  </div>
                                  <div className="af-edit-field">
                                    <label>Start Lon</label>
                                    <input
                                      type="number"
                                      value={editForm.startLon}
                                      onChange={(e) => setEditForm({ ...editForm, startLon: e.target.value })}
                                      step="0.000001"
                                    />
                                  </div>
                                  <div className="af-edit-field">
                                    <label>End Lat</label>
                                    <input
                                      type="number"
                                      value={editForm.endLat}
                                      onChange={(e) => setEditForm({ ...editForm, endLat: e.target.value })}
                                      step="0.000001"
                                    />
                                  </div>
                                  <div className="af-edit-field">
                                    <label>End Lon</label>
                                    <input
                                      type="number"
                                      value={editForm.endLon}
                                      onChange={(e) => setEditForm({ ...editForm, endLon: e.target.value })}
                                      step="0.000001"
                                    />
                                  </div>
                                  <div className="af-edit-field">
                                    <label>Heading</label>
                                    <input
                                      type="number"
                                      value={editForm.heading}
                                      onChange={(e) => setEditForm({ ...editForm, heading: e.target.value })}
                                      min="0"
                                      max="360"
                                      step="0.1"
                                    />
                                  </div>
                                </>
                              )}
                              <div className="af-edit-field af-checkbox">
                                <label>
                                  <input
                                    type="checkbox"
                                    checked={editForm.lighted || false}
                                    onChange={(e) => setEditForm({ ...editForm, lighted: e.target.checked })}
                                  />
                                  Lighted
                                </label>
                              </div>
                              <div className="af-edit-field af-checkbox">
                                <label>
                                  <input
                                    type="checkbox"
                                    checked={editForm.closed || false}
                                    onChange={(e) => setEditForm({ ...editForm, closed: e.target.checked })}
                                  />
                                  Closed
                                </label>
                              </div>
                            </div>
                            <div className="af-edit-actions">
                              <button onClick={saveFeature} disabled={saving} className="af-save-btn">
                                {saving ? 'Saving...' : 'Save Changes'}
                              </button>
                              <button onClick={cancelEdit} disabled={saving} className="af-cancel-btn">
                                Cancel
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  }

                  return (
                    <tr key={feature.id || feature.osmId || idx}>
                      <td>
                        <span
                          className="af-type-badge"
                          style={{ backgroundColor: typeConfig.color }}
                        >
                          {typeConfig.label}
                        </span>
                      </td>
                      <td>
                        <span className="af-ref">{feature.ref || '-'}</span>
                        {feature.name && feature.name !== feature.ref && (
                          <span className="af-name">{feature.name}</span>
                        )}
                      </td>
                      <td>{feature.surface || '-'}</td>
                      <td>
                        {feature.lengthMeters || feature.length
                          ? `${Math.round(feature.lengthMeters || feature.length)}m`
                          : '-'}
                        {(feature.widthMeters || feature.width) &&
                          ` x ${Math.round(feature.widthMeters || feature.width)}m`}
                      </td>
                      <td className="af-coord">
                        {feature.latitude != null
                          ? `${formatCoord(feature.latitude)}, ${formatCoord(feature.longitude)}`
                          : '-'}
                      </td>
                      <td>
                        <span className={`af-source-badge af-source-${(feature.source || 'osm').toLowerCase()}`}>
                          {feature.source || 'OSM'}
                        </span>
                      </td>
                      <td>
                        {feature.isManuallyModified ? (
                          <span className="af-modified">Yes</span>
                        ) : '-'}
                      </td>
                      <td className="af-actions-cell">
                        <button
                          onClick={() => startEdit(feature)}
                          className="af-action-btn af-edit-btn"
                          title="Edit feature"
                        >
                          Edit
                        </button>
                        {feature.id && (
                          <button
                            onClick={() => handleDelete(feature)}
                            className="af-action-btn af-delete-btn"
                            title="Delete feature"
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Conflict Dialog */}
      {showConflictDialog && conflicts.length > 0 && (
        <div className="af-conflict-overlay">
          <div className="af-conflict-dialog">
            <h3>Feature Conflicts Detected</h3>
            <p>The following features have been modified locally but have also changed in OSM. Choose which version to keep:</p>

            <div className="af-conflict-list">
              {conflicts.map((conflict, idx) => (
                <div key={idx} className="af-conflict-item">
                  <div className="af-conflict-header">
                    <span className="af-conflict-type">
                      {getFeatureTypeConfig(conflict.localVersion?.featureType).label}
                    </span>
                    <span className="af-conflict-ref">
                      {conflict.localVersion?.ref || conflict.localVersion?.name || 'Unnamed'}
                    </span>
                  </div>

                  {conflict.changeSummary && (
                    <div className="af-conflict-summary">{conflict.changeSummary}</div>
                  )}

                  <div className="af-conflict-comparison">
                    <div className="af-conflict-version af-local">
                      <h5>Local Version</h5>
                      <div className="af-version-details">
                        <div>Surface: {conflict.localVersion?.surface || '-'}</div>
                        <div>Length: {conflict.localVersion?.lengthMeters ? `${conflict.localVersion.lengthMeters}m` : '-'}</div>
                        <div>Width: {conflict.localVersion?.widthMeters ? `${conflict.localVersion.widthMeters}m` : '-'}</div>
                      </div>
                    </div>
                    <div className="af-conflict-version af-osm">
                      <h5>OSM Version</h5>
                      <div className="af-version-details">
                        <div>Surface: {conflict.osmVersion?.surface || '-'}</div>
                        <div>Length: {conflict.osmVersion?.lengthMeters ? `${conflict.osmVersion.lengthMeters}m` : '-'}</div>
                        <div>Width: {conflict.osmVersion?.widthMeters ? `${conflict.osmVersion.widthMeters}m` : '-'}</div>
                      </div>
                    </div>
                  </div>

                  <div className="af-conflict-actions">
                    <button
                      onClick={() => handleResolveConflict(conflict.localVersion?.id, 'KEEP_LOCAL')}
                      disabled={resolvingConflict === conflict.localVersion?.id}
                      className="af-resolve-btn af-keep-local"
                    >
                      Keep Local
                    </button>
                    <button
                      onClick={() => handleResolveConflict(conflict.localVersion?.id, 'USE_OSM')}
                      disabled={resolvingConflict === conflict.localVersion?.id}
                      className="af-resolve-btn af-use-osm"
                    >
                      Use OSM
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="af-dialog-footer">
              <button onClick={() => setShowConflictDialog(false)} className="af-close-btn">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AirportFeatures;
